import cloneDeep from 'lodash.clonedeep';
import { action, computed, observable, makeObservable } from 'mobx';
import { defaultNotificationSetting, defaultNotificationSettingValidation, NotificationsSettingPath } from '../constants/notifications';
import { getNotificationSettingParams } from '../helpers/notifications';
import { INotificationSetting, INotificationSettingValidation } from '../types/notifications';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { emptyNameValidation } from 'src/utils/validation';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

export default class NotificationSettingEditStore {
  private path;
  private defaultNotification = defaultNotificationSetting;

  constructor(path: NotificationsSettingPath, defaultNotification: INotificationSetting) {
    makeObservable(this);
    this.path = path;
    this.defaultNotification = defaultNotification;
  }

  private createAbortController = getAbortController();

  @observable notification: INotificationSetting = this.defaultNotification;

  @observable loading: LoadingStatus = LoadingStatus.Init;
  @observable isEdit: boolean = false;

  @computed get isLoading(): boolean {
    return this.loading === LoadingStatus.Loading;
  }

  @action initStore = () => {
    this.resetNotification();
  };

  @action changeNotification = (value: Partial<INotificationSetting>) => {
    this.notification = { ...this.notification, ...value };
    this.isEdit = true;

    // update validation
    if (!this.validation.isValid) {
      this.setValidation();
    }
  };

  @action resetNotification = () => {
    this.validation = cloneDeep(defaultNotificationSettingValidation);
    this.notification = this.defaultNotification;
    this.isEdit = false;
  };

  @action createNotification = async () => {
    this.createAbortController.abort();
    this.createAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params = getNotificationSettingParams(this.notification);

      await client.post(`v3/settings/notifications/${this.path}`, params, {
        signal: this.createAbortController.signal,
      });

      this.isEdit = false;
      this.loading = LoadingStatus.Success;
      return true;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'NotificationSettingEditStore createNotificationSetting', showModal: true });
    }
  };

  @observable validation: INotificationSettingValidation = cloneDeep(defaultNotificationSettingValidation);
  @action setValidation = () => {
    this.validation = cloneDeep(defaultNotificationSettingValidation);

    [this.validation.subject, this.validation.isValid] = emptyNameValidation(
      this.notification.subject,
      this.validation.subject,
      this.validation.isValid
    );

    [this.validation.message, this.validation.isValid] = emptyNameValidation(
      this.notification.message,
      this.validation.message,
      this.validation.isValid
    );

    return this.validation.isValid;
  };
}
